export enum SubscriptionPlan {
  STANDARD = 'STANDARD',
  PRO = 'PRO'
}

export enum SubscriptionStatus {
  TRIALING = 'TRIALING',
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  CANCELED = 'CANCELED',
  INCOMPLETE = 'INCOMPLETE'
}

export interface BillingUpcomingLine {
  description: string
  lineKind?: BillingStripeLineKind
  plan?: SubscriptionPlan
  periodStart?: string
  periodEnd?: string
  quantity?: number
  lineTotal: number
  baseAmount: number
  vatAmount: number
}

export enum BillingStripeLineKind {
  DOCUMENT_USAGE = 'DOCUMENT_USAGE',
  SEAT_RENEWAL = 'SEAT_RENEWAL',
  SEAT_ADDITION = 'SEAT_ADDITION',
  PRORATION_CREDIT = 'PRORATION_CREDIT',
  PRORATION_CHARGE = 'PRORATION_CHARGE',
  DISCOUNT = 'DISCOUNT',
  OTHER = 'OTHER'
}

export enum PlanChangeType {
  UPGRADE = 'UPGRADE',
  DOWNGRADE = 'DOWNGRADE',
  CANCEL_PENDING = 'CANCEL_PENDING',
  NONE = 'NONE'
}

export interface ChangePlanPreview {
  changeType: PlanChangeType
  currentPlan: SubscriptionPlan
  targetPlan: SubscriptionPlan
  immediateCharge: number
  effectiveAt?: string
  message: string
  previewLines?: BillingUpcomingLine[]
}

export interface SeatChangePreview {
  usedSeats: number
  committedSeats: number
  availableSeats: number
  requiresPayment: boolean
  immediateCharge: number
  effectiveAt?: string
  message: string
  previewLines?: BillingUpcomingLine[]
}

export interface BillingSummary {
  hasActiveSubscription: boolean
  plan: SubscriptionPlan
  status?: SubscriptionStatus
  originCode?: string
  originName?: string
  standardPrice: number
  proPrice: number
  documentUnitPrice: number
  vatRate: number
  billableSeats: number
  committedSeats?: number
  availableSeats?: number
  billableDocuments: number
  estimatedNextCharge: number
  recurringRenewalCharge?: number
  hasProrationAdjustments?: boolean
  upcomingLines?: BillingUpcomingLine[]
  pricesIncludeVat?: boolean
  trialEndsAt?: string
  currentPeriodStart?: string
  currentPeriodEnd?: string
  pendingPlan?: SubscriptionPlan
  planChangeEffectiveAt?: string
  billingCompanyId?: string
  billingCompanyName?: string
}

function toNumber(value: unknown, fallback = 0): number {
  if (value === null || value === undefined || value === '') {
    return fallback
  }
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(num) ? num : fallback
}

export function normalizeBillingSummary(data: BillingSummary): BillingSummary {
  return {
    ...data,
    standardPrice: toNumber(data.standardPrice),
    proPrice: toNumber(data.proPrice),
    documentUnitPrice: toNumber(data.documentUnitPrice, 0.25),
    vatRate: toNumber(data.vatRate, 21),
    billableSeats: toNumber(data.billableSeats),
    committedSeats: data.committedSeats != null ? toNumber(data.committedSeats) : undefined,
    availableSeats: data.availableSeats != null ? toNumber(data.availableSeats) : undefined,
    billableDocuments: toNumber(data.billableDocuments),
    estimatedNextCharge: toNumber(data.estimatedNextCharge),
    recurringRenewalCharge: toNumber(data.recurringRenewalCharge, toNumber(data.estimatedNextCharge)),
    hasProrationAdjustments: data.hasProrationAdjustments ?? false,
    upcomingLines: (data.upcomingLines ?? []).map((line) => ({
      description: line.description,
      lineKind: line.lineKind,
      plan: line.plan,
      periodStart: line.periodStart,
      periodEnd: line.periodEnd,
      quantity: line.quantity != null ? toNumber(line.quantity) : undefined,
      lineTotal: toNumber(line.lineTotal),
      baseAmount: toNumber(line.baseAmount),
      vatAmount: toNumber(line.vatAmount)
    })),
    pricesIncludeVat: data.pricesIncludeVat ?? true,
    pendingPlan: data.pendingPlan,
    planChangeEffectiveAt: data.planChangeEffectiveAt,
    billingCompanyId: data.billingCompanyId,
    billingCompanyName: data.billingCompanyName
  }
}

export function normalizeKargoBill(data: KargoBill): KargoBill {
  return {
    ...data,
    baseAmount: toNumber(data.baseAmount),
    vatAmount: toNumber(data.vatAmount),
    totalAmount: toNumber(data.totalAmount)
  }
}

export interface BillingOrigin {
  code: string
  name: string
}

export interface KargoBill {
  id: string
  number: string
  reference?: string
  date: string
  baseAmount: number
  vatAmount: number
  totalAmount: number
  status: string
}
